import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { JSONPath } from 'jsonpath-plus';
import { Observable, Subject, catchError, finalize, map, of, switchMap, takeUntil } from 'rxjs';
import { DuplicateOrderList, OrderDetail } from 'src/app/module/order/interface/order.interface';
import {
  ConfirmDialogComponent,
  ConfirmDialogConfig,
} from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { BillingConfirmation } from 'src/app/shared/interface/billing.interface';
import { EnumRefPipe } from 'src/app/shared/pipes/enum-ref.pipe';
import { BillingService } from 'src/app/shared/services/billing.service';
import { ProgressLoaderService } from 'src/app/shared/services/progress-loader.service';
import { ProxyService } from 'src/app/shared/services/proxy.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { AppState } from 'src/app/shared/state/app.state';
import { CompanyState } from 'src/app/shared/state/company.state';
import { OrderStatus } from '../../order-list/interface/order-list.interface';
import { OrderService } from '../../order/service/order.service';
import { NewOrderApi } from '../api/new-order.api';
import { BillingDialogComponent } from '../component/billing-dialog/billing-dialog.component';
import {
  AllowedReportGroup,
  DuplicateOrderParam,
  InitiateOrderApiResponse,
  NewOrder,
  PropertySearchCriteria,
  SearchMode,
  SearchedPropertyData,
} from '../interface/new-order.interface';

@Injectable({
  providedIn: 'root',
})
export class NewOrderService {
  orderStatus = OrderStatus;

  onDestroyNotifier$ = new Subject<void>();

  constructor(
    private _dialog: MatDialog,
    private _router: Router,
    private newOrderApi: NewOrderApi,
    private progressLoaderService: ProgressLoaderService,
    private proxyService: ProxyService,
    private snackBarService: SnackBarService,
    private appState: AppState,
    private billingService: BillingService,
    private orderService: OrderService,
    private companyState: CompanyState,
    private enumRefPipe: EnumRefPipe
  ) {}

  searchProperty(searchCriteria: PropertySearchCriteria, mode: SearchMode): Observable<SearchedPropertyData> {
    return this.proxyService.searchProperty(searchCriteria, mode);
  }

  searchDuplicateOrders(searchCriteria: DuplicateOrderParam): Observable<DuplicateOrderList[]> {
    const loaderId = this.progressLoaderService.show('Checking for Duplicates');
    return this.newOrderApi.searchDuplicateOrders(searchCriteria).pipe(
      catchError(() => of([])),
      finalize(() => this.progressLoaderService.hide(loaderId))
    );
  }

  /** Creates the order. `companyId` comes from the preceding eligibility check and is used if the payment fails. */
  createOrder(newOrder: NewOrder | null, companyId?: string): Observable<InitiateOrderApiResponse> {
    const loaderId = this.progressLoaderService.show('Creating Order');
    return this.newOrderApi.createOrder({ ...newOrder }).pipe(
      map((res: InitiateOrderApiResponse) => {
        if (res.orderStatus === this.orderStatus.automated) {
          this.showInfoDialog();
        } else if (res.orderStatus === this.orderStatus.paymentFailed) {
          this.billingService.checkPaymentStatus(companyId);
        } else {
          this.orderService.clearState();
          this._router.navigate(['orders', res.id], { replaceUrl: true, queryParamsHandling: 'preserve' });
        }
        return res;
      }),
      catchError((error) => {
        this.snackBarService.open('error', error.error.message);
        return of({} as InitiateOrderApiResponse);
      }),
      finalize(() => this.progressLoaderService.hide(loaderId))
    );
  }

  showInfoDialog() {
    const config: ConfirmDialogConfig<unknown> = {
      primaryButtonLabel: {
        label: 'OK',
      },
      description: `<p>We're currently setting up this order.</p>\
                    <p>You can begin work once the status changes from\
                    <strong>Automation In Progress</strong>\
                    <img src="assets/icon/automation.svg" width="18" style="vertical-align: middle; margin-right: 4px;" />\
                    &nbsp;to&nbsp;\
                    <strong>In Progress</strong>\
                    <img src="assets/icon/in_progress.svg" width="18" style="vertical-align: middle; margin-right: 4px;" />\
                    </p>`,
      title: 'Setting Up Your Order',
      actionMessage: {
        message: `To check the latest status, click on the order and select <strong>"Refresh Status"</strong>.`,
      },
    };

    const dialogRef = this._dialog.open<ConfirmDialogComponent>(ConfirmDialogComponent, {
      data: config,
    });

    dialogRef.componentInstance.action.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((result) => {
      if (!result || !result.submitted) {
        dialogRef.close();
      } else {
        this._router.navigate(['/orders']);
        this._dialog.closeAll();
      }
    });
  }

  /**
   * Runs the billing eligibility check for an order and, unless the company is on a contractual plan,
   * asks the user to confirm the charge. Emits whether order creation may go ahead.
   */
  confirmBillingEligibility(order: NewOrder | OrderDetail | null): Observable<BillingConfirmation> {
    const billingProductId = order ? this.getBillingProductId(order) : '';

    return this.billingService.checkBillingEligibility(billingProductId).pipe(
      switchMap((eligibility) => {
        const companyId = eligibility.data?.companyId;

        if (eligibility.status === 'FAILED') {
          this.snackBarService.open('error', eligibility.message);
          return of<BillingConfirmation>({ proceed: false, companyId });
        }

        // A contractual company is already covered, so there is nothing for the user to confirm.
        if (eligibility.data?.orderTypeBillingModel === 'CONTRACTUAL') {
          return of<BillingConfirmation>({ proceed: true, companyId });
        }

        // The billing dialog cannot be dismissed, so never open it for a response we have no config for.
        if (!this.billingService.getBillingDialogConfigFromApiResponse(eligibility)) {
          this.snackBarService.open('error', eligibility.message ?? 'Unable to verify billing eligibility.');
          return of<BillingConfirmation>({ proceed: false, companyId });
        }

        const dialogRef = this._dialog.open(BillingDialogComponent, {
          disableClose: true,
          data: eligibility,
        });

        return dialogRef
          .afterClosed()
          .pipe(map((result): BillingConfirmation => ({ proceed: result?.proceed === true, companyId })));
      })
    );
  }

  public getBillingProductId(order: NewOrder | OrderDetail): string {
    const reportType = order.reportType ?? '';
    const billingKeyConfigs = this.appState.applicationSettings.billingKeyConfig;

    // Find a matching billing config for this order
    const matchingConfig = billingKeyConfigs.find((config) => {
      // Check if reportType matches
      if (config.reportType !== reportType) {
        return false;
      }

      // Use JSONPath to get the value from the order object
      const extractedValue: string = JSONPath({ json: order, path: config.orderJsonPath, wrap: false });

      // Check if the extracted value matches the orderValue in the config
      return extractedValue === config.orderValue;
    });

    // If match found, return billingKey|reportType, otherwise return just reportType
    if (matchingConfig) {
      return `${matchingConfig.billingKey}|${reportType}`;
    }

    return reportType;
  }

  public getAllowedReportGroups(): AllowedReportGroup[] {
    const allowedReportGroupList: AllowedReportGroup[] = [];

    this.companyState.enabledReportList.forEach((reportType) => {
      const orderSetting = this.companyState.companySetting[reportType];

      const existingReportCategoryIndex = allowedReportGroupList.findIndex(
        (reportGroup) => reportGroup.reportCategory == orderSetting.reportCategory
      );

      if (existingReportCategoryIndex >= 0) {
        const existingReportParentIndex = allowedReportGroupList[existingReportCategoryIndex].reportList?.findIndex(
          (report) => report.reportType == orderSetting.reportParent
        );

        if (existingReportParentIndex != undefined && existingReportParentIndex >= 0) {
          allowedReportGroupList[existingReportCategoryIndex].reportList[
            existingReportParentIndex
          ].reportFormList?.push({
            key: reportType,
            displayValue: this.enumRefPipe.transform(reportType, 'reportType') ?? '',
          });
        } else {
          allowedReportGroupList[existingReportCategoryIndex].reportList.push({
            reportType: reportType,
            reportDescription: orderSetting.reportDescription,
            reportFormList: [
              {
                key: reportType,
                displayValue: this.enumRefPipe.transform(reportType, 'reportType') ?? '',
              },
            ],
          });
        }
      } else {
        allowedReportGroupList.push({
          reportCategory: orderSetting.reportCategory,
          reportList: [
            {
              reportType: reportType,
              reportDescription: orderSetting.reportDescription,
              reportFormList: [
                {
                  key: reportType,
                  displayValue: this.enumRefPipe.transform(reportType, 'reportType') ?? '',
                },
              ],
            },
          ],
        });
      }
    });

    return allowedReportGroupList;
  }
}
